import { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { useCart } from '@/contexts/CartContext';
import { useAuth } from '@/contexts/AuthContext';
import { useCurrency } from '@/contexts/CurrencyContext';
import DualPrice, { CheckoutPrices } from '@/components/DualPrice';
import CurrencyMismatchWarning from '@/components/CurrencyMismatchWarning';
import { getCurrencyForCountry, GATEWAY_CURRENCIES, formatPrice, convertPrice, ALL_CURRENCIES } from '@/utils/currencyDetection';
import { toast } from 'sonner';
import { CreditCard, Building2, Wallet, Tag, X, Check, Globe, MapPin, Smartphone, Hash } from 'lucide-react';
import { trackInitiateCheckout, trackPurchase } from '@/components/TrackingPixels';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// All countries of the world organized by region
const COUNTRIES_BY_REGION = {
  "Africa": [
    { code: 'DZ', name: 'Algeria' },
    { code: 'AO', name: 'Angola' },
    { code: 'BJ', name: 'Benin' },
    { code: 'BW', name: 'Botswana' },
    { code: 'BF', name: 'Burkina Faso' },
    { code: 'BI', name: 'Burundi' },
    { code: 'CV', name: 'Cabo Verde' },
    { code: 'CM', name: 'Cameroon' },
    { code: 'CF', name: 'Central African Republic' },
    { code: 'TD', name: 'Chad' },
    { code: 'KM', name: 'Comoros' },
    { code: 'CG', name: 'Congo' },
    { code: 'CD', name: 'Congo (DRC)' },
    { code: 'CI', name: "Côte d'Ivoire" },
    { code: 'DJ', name: 'Djibouti' },
    { code: 'EG', name: 'Egypt' },
    { code: 'GQ', name: 'Equatorial Guinea' },
    { code: 'ER', name: 'Eritrea' },
    { code: 'SZ', name: 'Eswatini' },
    { code: 'ET', name: 'Ethiopia' },
    { code: 'GA', name: 'Gabon' },
    { code: 'GM', name: 'Gambia' },
    { code: 'GH', name: 'Ghana' },
    { code: 'GN', name: 'Guinea' },
    { code: 'GW', name: 'Guinea-Bissau' },
    { code: 'KE', name: 'Kenya' },
    { code: 'LS', name: 'Lesotho' },
    { code: 'LR', name: 'Liberia' },
    { code: 'LY', name: 'Libya' },
    { code: 'MG', name: 'Madagascar' },
    { code: 'MW', name: 'Malawi' },
    { code: 'ML', name: 'Mali' },
    { code: 'MR', name: 'Mauritania' },
    { code: 'MU', name: 'Mauritius' },
    { code: 'MA', name: 'Morocco' },
    { code: 'MZ', name: 'Mozambique' },
    { code: 'NA', name: 'Namibia' },
    { code: 'NE', name: 'Niger' },
    { code: 'NG', name: 'Nigeria' },
    { code: 'RW', name: 'Rwanda' },
    { code: 'ST', name: 'São Tomé and Príncipe' },
    { code: 'SN', name: 'Senegal' },
    { code: 'SC', name: 'Seychelles' },
    { code: 'SL', name: 'Sierra Leone' },
    { code: 'SO', name: 'Somalia' },
    { code: 'ZA', name: 'South Africa' },
    { code: 'SS', name: 'South Sudan' },
    { code: 'SD', name: 'Sudan' },
    { code: 'TZ', name: 'Tanzania' },
    { code: 'TG', name: 'Togo' },
    { code: 'TN', name: 'Tunisia' },
    { code: 'UG', name: 'Uganda' },
    { code: 'ZM', name: 'Zambia' },
    { code: 'ZW', name: 'Zimbabwe' }
  ],
  "Asia": [
    { code: 'AF', name: 'Afghanistan' },
    { code: 'AM', name: 'Armenia' },
    { code: 'AZ', name: 'Azerbaijan' },
    { code: 'BH', name: 'Bahrain' },
    { code: 'BD', name: 'Bangladesh' },
    { code: 'BT', name: 'Bhutan' },
    { code: 'BN', name: 'Brunei' },
    { code: 'KH', name: 'Cambodia' },
    { code: 'CN', name: 'China' },
    { code: 'CY', name: 'Cyprus' },
    { code: 'GE', name: 'Georgia' },
    { code: 'IN', name: 'India' },
    { code: 'ID', name: 'Indonesia' },
    { code: 'IR', name: 'Iran' },
    { code: 'IQ', name: 'Iraq' },
    { code: 'IL', name: 'Israel' },
    { code: 'JP', name: 'Japan' },
    { code: 'JO', name: 'Jordan' },
    { code: 'KZ', name: 'Kazakhstan' },
    { code: 'KW', name: 'Kuwait' },
    { code: 'KG', name: 'Kyrgyzstan' },
    { code: 'LA', name: 'Laos' },
    { code: 'LB', name: 'Lebanon' },
    { code: 'MY', name: 'Malaysia' },
    { code: 'MV', name: 'Maldives' },
    { code: 'MN', name: 'Mongolia' },
    { code: 'MM', name: 'Myanmar' },
    { code: 'NP', name: 'Nepal' },
    { code: 'KP', name: 'North Korea' },
    { code: 'OM', name: 'Oman' },
    { code: 'PK', name: 'Pakistan' },
    { code: 'PS', name: 'Palestine' },
    { code: 'PH', name: 'Philippines' },
    { code: 'QA', name: 'Qatar' },
    { code: 'SA', name: 'Saudi Arabia' },
    { code: 'SG', name: 'Singapore' },
    { code: 'KR', name: 'South Korea' },
    { code: 'LK', name: 'Sri Lanka' },
    { code: 'SY', name: 'Syria' },
    { code: 'TW', name: 'Taiwan' },
    { code: 'TJ', name: 'Tajikistan' },
    { code: 'TH', name: 'Thailand' },
    { code: 'TL', name: 'Timor-Leste' },
    { code: 'TR', name: 'Turkey' },
    { code: 'TM', name: 'Turkmenistan' },
    { code: 'AE', name: 'United Arab Emirates' },
    { code: 'UZ', name: 'Uzbekistan' },
    { code: 'VN', name: 'Vietnam' },
    { code: 'YE', name: 'Yemen' }
  ],
  "Europe": [
    { code: 'AL', name: 'Albania' },
    { code: 'AD', name: 'Andorra' },
    { code: 'AT', name: 'Austria' },
    { code: 'BY', name: 'Belarus' },
    { code: 'BE', name: 'Belgium' },
    { code: 'BA', name: 'Bosnia and Herzegovina' },
    { code: 'BG', name: 'Bulgaria' },
    { code: 'HR', name: 'Croatia' },
    { code: 'CZ', name: 'Czech Republic' },
    { code: 'DK', name: 'Denmark' },
    { code: 'EE', name: 'Estonia' },
    { code: 'FI', name: 'Finland' },
    { code: 'FR', name: 'France' },
    { code: 'DE', name: 'Germany' },
    { code: 'GR', name: 'Greece' },
    { code: 'HU', name: 'Hungary' },
    { code: 'IS', name: 'Iceland' },
    { code: 'IE', name: 'Ireland' },
    { code: 'IT', name: 'Italy' },
    { code: 'XK', name: 'Kosovo' },
    { code: 'LV', name: 'Latvia' },
    { code: 'LI', name: 'Liechtenstein' },
    { code: 'LT', name: 'Lithuania' },
    { code: 'LU', name: 'Luxembourg' },
    { code: 'MT', name: 'Malta' },
    { code: 'MD', name: 'Moldova' },
    { code: 'MC', name: 'Monaco' },
    { code: 'ME', name: 'Montenegro' },
    { code: 'NL', name: 'Netherlands' },
    { code: 'MK', name: 'North Macedonia' },
    { code: 'NO', name: 'Norway' },
    { code: 'PL', name: 'Poland' },
    { code: 'PT', name: 'Portugal' },
    { code: 'RO', name: 'Romania' },
    { code: 'RU', name: 'Russia' },
    { code: 'SM', name: 'San Marino' },
    { code: 'RS', name: 'Serbia' },
    { code: 'SK', name: 'Slovakia' },
    { code: 'SI', name: 'Slovenia' },
    { code: 'ES', name: 'Spain' },
    { code: 'SE', name: 'Sweden' },
    { code: 'CH', name: 'Switzerland' },
    { code: 'UA', name: 'Ukraine' },
    { code: 'GB', name: 'United Kingdom' },
    { code: 'VA', name: 'Vatican City' }
  ],
  "North America": [
    { code: 'AG', name: 'Antigua and Barbuda' },
    { code: 'BS', name: 'Bahamas' },
    { code: 'BB', name: 'Barbados' },
    { code: 'BZ', name: 'Belize' },
    { code: 'CA', name: 'Canada' },
    { code: 'CR', name: 'Costa Rica' },
    { code: 'CU', name: 'Cuba' },
    { code: 'DM', name: 'Dominica' },
    { code: 'DO', name: 'Dominican Republic' },
    { code: 'SV', name: 'El Salvador' },
    { code: 'GD', name: 'Grenada' },
    { code: 'GT', name: 'Guatemala' },
    { code: 'HT', name: 'Haiti' },
    { code: 'HN', name: 'Honduras' },
    { code: 'JM', name: 'Jamaica' },
    { code: 'MX', name: 'Mexico' },
    { code: 'NI', name: 'Nicaragua' },
    { code: 'PA', name: 'Panama' },
    { code: 'KN', name: 'Saint Kitts and Nevis' },
    { code: 'LC', name: 'Saint Lucia' },
    { code: 'VC', name: 'Saint Vincent and the Grenadines' },
    { code: 'TT', name: 'Trinidad and Tobago' },
    { code: 'US', name: 'United States' }
  ],
  "South America": [
    { code: 'AR', name: 'Argentina' },
    { code: 'BO', name: 'Bolivia' },
    { code: 'BR', name: 'Brazil' },
    { code: 'CL', name: 'Chile' },
    { code: 'CO', name: 'Colombia' },
    { code: 'EC', name: 'Ecuador' },
    { code: 'GY', name: 'Guyana' },
    { code: 'PY', name: 'Paraguay' },
    { code: 'PE', name: 'Peru' },
    { code: 'SR', name: 'Suriname' },
    { code: 'UY', name: 'Uruguay' },
    { code: 'VE', name: 'Venezuela' }
  ],
  "Oceania": [
    { code: 'AU', name: 'Australia' },
    { code: 'FJ', name: 'Fiji' },
    { code: 'KI', name: 'Kiribati' },
    { code: 'MH', name: 'Marshall Islands' },
    { code: 'FM', name: 'Micronesia' },
    { code: 'NR', name: 'Nauru' },
    { code: 'NZ', name: 'New Zealand' },
    { code: 'PW', name: 'Palau' },
    { code: 'PG', name: 'Papua New Guinea' },
    { code: 'WS', name: 'Samoa' },
    { code: 'SB', name: 'Solomon Islands' },
    { code: 'TO', name: 'Tonga' },
    { code: 'TV', name: 'Tuvalu' },
    { code: 'VU', name: 'Vanuatu' }
  ]
};

// Create flat sorted list of all countries
const ALL_COUNTRIES = Object.values(COUNTRIES_BY_REGION)
  .flat()
  .sort((a, b) => a.name.localeCompare(b.name));

// Payment gateway logos using actual brand images
const GATEWAY_LOGOS = {
  stripe: (
    <div className="flex items-center gap-2">
      <img 
        src="https://customer-assets.emergentagent.com/job_64e18541-7b82-4a99-8c28-ccebd0678113/artifacts/9izynr36_Stripe_Logo%2C_revised_2016.svg.png" 
        alt="Stripe" 
        className="h-6 w-auto" 
      />
    </div>
  ),
  paypal: (
    <div className="flex items-center gap-2">
      <img 
        src="https://customer-assets.emergentagent.com/job_76a64848-dec5-44d6-89f0-09d8daca8d14/artifacts/0np9ivws_pngimg.com%20-%20paypal_PNG7.png" 
        alt="PayPal" 
        className="h-7 w-auto" 
      />
    </div>
  ),
  paystack: (
    <div className="flex items-center gap-2">
      <img 
        src="https://customer-assets.emergentagent.com/job_76a64848-dec5-44d6-89f0-09d8daca8d14/artifacts/kwon3tu5_paystack-logo-paystack-logo-GGkxsimJ_t.jpg" 
        alt="Paystack" 
        className="h-6 w-auto" 
      />
    </div>
  ),
  flutterwave: (
    <div className="flex items-center gap-2">
      <img 
        src="https://customer-assets.emergentagent.com/job_64e18541-7b82-4a99-8c28-ccebd0678113/artifacts/pzbajfir_flutterwave.jpeg" 
        alt="Flutterwave" 
        className="h-8 w-8 rounded" 
      />
      <span className="font-semibold text-[#F5A623]">Flutterwave</span>
    </div>
  ),
  korapay: (
    <div className="flex items-center gap-2">
      <img 
        src="https://customer-assets.emergentagent.com/job_76a64848-dec5-44d6-89f0-09d8daca8d14/artifacts/my7dz84a_korapay-logo-png_seeklogo-490134.png" 
        alt="Korapay" 
        className="h-6 w-auto" 
      />
    </div>
  ),
  squad: (
    <div className="flex items-center gap-2">
      <span className="px-2 py-0.5 rounded-md bg-[#2b1a6b] text-white text-sm font-bold tracking-wide">Squad</span>
      <span className="text-xs text-gray-500">by GTCO</span>
    </div>
  ),
  bachs: (
    <div className="flex items-center gap-2">
      <span className="px-2 py-0.5 rounded-md bg-gray-900 text-white text-sm font-bold tracking-widest">BACHS</span>
    </div>
  ),
  monnify: (
    <div className="flex items-center gap-2">
      <img 
        src="https://customer-assets.emergentagent.com/job_76a64848-dec5-44d6-89f0-09d8daca8d14/artifacts/h2y010zl_monnify%20logo.png" 
        alt="Monnify" 
        className="h-7 w-auto" 
      />
    </div>
  ),
  paysafe: (
    <div className="flex items-center gap-2">
      <img 
        src="https://customer-assets.emergentagent.com/job_64e18541-7b82-4a99-8c28-ccebd0678113/artifacts/ar4gvkwk_Paysafe_Logo.svg" 
        alt="Paysafe" 
        className="h-7 w-auto" 
      />
      <div className="flex flex-col">
        <span className="font-normal text-gray-600">(Nigerian Bank Transfer)</span>
        <span className="text-xs text-green-600">0% fees • Instant confirmation</span>
      </div>
    </div>
  )
};

// Gateway icons mapping (fallback)
const GATEWAY_ICONS = {
  stripe: <CreditCard className="mr-2 h-5 w-5" />,
  paypal: <Wallet className="mr-2 h-5 w-5" />,
  paysafe: <Building2 className="mr-2 h-5 w-5" />,
  paystack: <CreditCard className="mr-2 h-5 w-5" />,
  flutterwave: <CreditCard className="mr-2 h-5 w-5" />,
  korapay: <Building2 className="mr-2 h-5 w-5" />,
  squad: <CreditCard className="mr-2 h-5 w-5" />,
  bachs: <CreditCard className="mr-2 h-5 w-5" />,
  monnify: <Building2 className="mr-2 h-5 w-5" />
};

const RETAINED_GATEWAYS = ['paystack', 'flutterwave', 'bachs', 'korapay', 'squad'];
const REFERRAL_KEY = 'pactac_referral';

// Typed code wins; otherwise the last affiliate link clicked within its attribution window
const getReferralCode = (typed) => {
  if (typed && typed.trim()) return typed.trim().toUpperCase();
  try {
    const saved = JSON.parse(localStorage.getItem(REFERRAL_KEY) || 'null');
    if (saved && saved.expires_at > Date.now()) return saved.code;
  } catch (e) { /* ignore malformed storage */ }
  return null;
};

const GATEWAY_LABELS = {
  stripe: 'Credit Card (Stripe)',
  paypal: 'PayPal',
  paysafe: 'Paysafe (Nigerian Bank Transfer)',
  paystack: 'Paystack',
  flutterwave: 'Flutterwave',
  korapay: 'Korapay',
  squad: 'Squad',
  bachs: 'BACHs',
  monnify: 'Monnify'
};

// Payment methods available per gateway per currency
const GATEWAY_PAYMENT_METHODS = {
  stripe: {
    default: ['Card'],
    USD: ['Card', 'Apple Pay', 'Google Pay'],
    EUR: ['Card', 'Apple Pay', 'Google Pay', 'SEPA', 'Bancontact', 'iDEAL'],
    GBP: ['Card', 'Apple Pay', 'Google Pay'],
    NGN: ['Card'],
  },
  paypal: {
    default: ['PayPal Balance', 'Card'],
    USD: ['PayPal Balance', 'Card', 'Pay Later'],
    EUR: ['PayPal Balance', 'Card'],
    GBP: ['PayPal Balance', 'Card'],
  },
  paystack: {
    default: ['Card'],
    NGN: ['Card', 'Bank Transfer', 'USSD', 'Mobile Money'],
    GHS: ['Card', 'Mobile Money'],
    ZAR: ['Card'],
    KES: ['Card', 'M-Pesa'],
    USD: ['Card'],
  },
  flutterwave: {
    default: ['Card'],
    NGN: ['Card', 'Bank Transfer', 'USSD', 'Barter'],
    GHS: ['Card', 'Mobile Money'],
    KES: ['Card', 'M-Pesa'],
    ZAR: ['Card'],
    TZS: ['Card', 'Mobile Money'],
    UGX: ['Card', 'Mobile Money'],
    USD: ['Card'],
    EUR: ['Card'],
    GBP: ['Card'],
    XOF: ['Card', 'Mobile Money'],
    XAF: ['Card', 'Mobile Money'],
  },
  squad: {
    default: ['Card'],
    NGN: ['Card', 'Bank Transfer', 'USSD'],
    USD: ['Card'],
  },
  bachs: {
    default: ['Card'],
    NGN: ['Card', 'Bank Transfer'],
    USD: ['Card'],
  },
  korapay: {
    default: ['Card'],
    NGN: ['Card', 'Bank Transfer', 'Mobile Money'],
    GHS: ['Card', 'Mobile Money'],
    KES: ['Card', 'M-Pesa'],
  },
  monnify: {
    default: ['Bank Transfer'],
    NGN: ['Bank Transfer', 'Card', 'USSD'],
  },
  paysafe: {
    default: ['Bank Transfer'],
    NGN: ['Bank Transfer'],
  },
};

// Payment method icons and display names
const PAYMENT_METHOD_CONFIG = {
  'Card': { 
    icon: CreditCard, 
    label: 'Debit/Credit Card', 
    description: 'Pay with Visa, Mastercard, Verve',
    color: 'bg-blue-50 border-blue-200 hover:border-blue-400'
  },
  'Bank Transfer': { 
    icon: Building2, 
    label: 'Bank Transfer', 
    description: 'Pay directly from your bank account',
    color: 'bg-green-50 border-green-200 hover:border-green-400'
  },
  'Mobile Money': { 
    icon: Smartphone, 
    label: 'Mobile Money', 
    description: 'Pay with MTN MoMo, Airtel Money, etc.',
    color: 'bg-yellow-50 border-yellow-200 hover:border-yellow-400'
  },
  'M-Pesa': { 
    icon: Smartphone, 
    label: 'M-Pesa', 
    description: 'Pay with Safaricom M-Pesa',
    color: 'bg-green-50 border-green-200 hover:border-green-400'
  },
  'USSD': { 
    icon: Hash, 
    label: 'USSD', 
    description: 'Pay using USSD code on any phone',
    color: 'bg-purple-50 border-purple-200 hover:border-purple-400'
  },
  'PayPal Balance': { 
    icon: Wallet, 
    label: 'PayPal', 
    description: 'Pay with your PayPal account',
    color: 'bg-blue-50 border-blue-200 hover:border-blue-400'
  },
  'Apple Pay': { 
    icon: CreditCard, 
    label: 'Apple Pay', 
    description: 'Pay with Apple Pay',
    color: 'bg-gray-50 border-gray-300 hover:border-gray-400'
  },
  'Google Pay': { 
    icon: CreditCard, 
    label: 'Google Pay', 
    description: 'Pay with Google Pay',
    color: 'bg-gray-50 border-gray-300 hover:border-gray-400'
  },
  'Barter': { 
    icon: Wallet, 
    label: 'Barter by Flutterwave', 
    description: 'Pay with Barter wallet',
    color: 'bg-orange-50 border-orange-200 hover:border-orange-400'
  },
  'SEPA': { 
    icon: Building2, 
    label: 'SEPA Bank Transfer', 
    description: 'European bank transfer',
    color: 'bg-blue-50 border-blue-200 hover:border-blue-400'
  },
  'iDEAL': { 
    icon: Building2, 
    label: 'iDEAL', 
    description: 'Dutch online banking',
    color: 'bg-pink-50 border-pink-200 hover:border-pink-400'
  },
  'Bancontact': { 
    icon: CreditCard, 
    label: 'Bancontact', 
    description: 'Belgian debit card',
    color: 'bg-blue-50 border-blue-200 hover:border-blue-400'
  },
  'Pay Later': { 
    icon: Wallet, 
    label: 'Pay Later', 
    description: 'Buy now, pay later with PayPal',
    color: 'bg-yellow-50 border-yellow-200 hover:border-yellow-400'
  },
};

const CheckoutPage = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const navigate = useNavigate();
  const { cart, getCartTotal, clearCart } = useCart();
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [enabledGateways, setEnabledGateways] = useState([]);
  const [loadingGateways, setLoadingGateways] = useState(true);
  const [loadingRate, setLoadingRate] = useState(false);
  const [states, setStates] = useState([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [detectingLocation, setDetectingLocation] = useState(true);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [referralInput, setReferralInput] = useState(() => getReferralCode('') || '');
  
  // Checkout settings state
  const [checkoutSettings, setCheckoutSettings] = useState({
    show_promo_code: true,
    show_delivery_options: true
  });
  const [deliveryOptions, setDeliveryOptions] = useState([]);
  const [selectedDeliveryOption, setSelectedDeliveryOption] = useState(null);
  
  // New state for payment method selection
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [showGatewaySelector, setShowGatewaySelector] = useState(false);
  const [availableGatewaysForMethod, setAvailableGatewaysForMethod] = useState([]);
  const [gatewaySelectionTimeout, setGatewaySelectionTimeout] = useState(null);
  const [countdownSeconds, setCountdownSeconds] = useState(3);
  
  // Estimated delivery - calculated once on mount (5-8 days with random time)
  const [estimatedDelivery] = useState(() => {
    const daysToAdd = Math.floor(Math.random() * 4) + 5; // 5, 6, 7, or 8
    const deliveryDate = new Date();
    deliveryDate.setDate(deliveryDate.getDate() + daysToAdd);
    
    // Random time between 9 AM and 6 PM
    const hour = Math.floor(Math.random() * 9) + 9; // 9-17
    const minute = Math.random() < 0.5 ? '00' : '30';
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour > 12 ? hour - 12 : hour;
    
    // Format: "Friday, February 28th at 2:30 PM"
    const dayName = deliveryDate.toLocaleDateString('en-US', { weekday: 'long' });
    const monthName = deliveryDate.toLocaleDateString('en-US', { month: 'long' });
    const day = deliveryDate.getDate();
    const suffix = day === 1 || day === 21 || day === 31 ? 'st' 
                 : day === 2 || day === 22 ? 'nd' 
                 : day === 3 || day === 23 ? 'rd' : 'th';
    
    return `${dayName}, ${monthName} ${day}${suffix} at ${displayHour}:${minute} ${ampm}`;
  });
  
  // Get exchange rates and active currency from currency context
  const { exchangeRates, activeCurrency } = useCurrency();
  const exchangeRate = exchangeRates?.NGN || 1550; // Legacy support
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  
  // Currency choice state - when display and payment currencies differ
  const [chosenPaymentCurrency, setChosenPaymentCurrency] = useState(null);
  const [showCurrencyModal, setShowCurrencyModal] = useState(false);
  
  // Guest checkout info
  const [guestInfo, setGuestInfo] = useState({
    email: '',
    phone: ''
  });
  
  const [shippingAddress, setShippingAddress] = useState({
    full_name: user?.full_name || '',
    address_line1: '',
    city: '',
    state: '',
    postal_code: '',
    country: ''
  });

  const [billingAddress, setBillingAddress] = useState({
    full_name: '',
    address_line1: '',
    city: '',
    state: '',
    postal_code: '',
    country: ''
  });

  const [billingSameAsShipping, setBillingSameAsShipping] = useState(true);
  const [recommendedProducts, setRecommendedProducts] = useState([]);

  // Check if guest checkout (no logged in user)
  const isGuest = !user || !token;

  // Helper to get country name from code
  const getCountryName = (code) => {
    const country = ALL_COUNTRIES.find(c => c.code === code);
    return country ? country.name : code;
  };

  // Check if customer is Nigerian
  const isNigerian = shippingAddress.country === 'NG' || shippingAddress.country === 'Nigeria';

  // Get total in NGN for Paysafe using live rate
  const getTotalInNGN = () => {
    const total = appliedCoupon ? appliedCoupon.final_total : getCartTotal();
    return Math.round(total * exchangeRate);
  };

  // Get final total after discount and delivery fee
  const getFinalTotal = () => {
    let total = appliedCoupon ? appliedCoupon.final_total : getCartTotal();
    if (selectedDeliveryOption) {
      total += selectedDeliveryOption.price;
    }
    return total;
  };

  // Get delivery fee
  const getDeliveryFee = () => {
    return selectedDeliveryOption ? selectedDeliveryOption.price : 0;
  };

  // Apply coupon code
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error('Please enter a coupon code');
      return;
    }

    setApplyingCoupon(true);
    try {
      const response = await axios.post(`${API}/coupons/apply`, {
        code: couponCode.trim(),
        order_total: getCartTotal()
      });
      
      setAppliedCoupon(response.data);
      toast.success(`Coupon applied! You save $${response.data.discount_amount.toFixed(2)}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid coupon code');
      setAppliedCoupon(null);
    } finally {
      setApplyingCoupon(false);
    }
  };

  // Remove applied coupon
  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    toast.info('Coupon removed');
  };

  // Fetch states/provinces for selected country
  const fetchStates = async (countryCode) => {
    if (!countryCode) {
      setStates([]);
      return;
    }
    
    setLoadingStates(true);
    try {
      const countryName = getCountryName(countryCode);
      const response = await axios.get(
        `https://countriesnow.space/api/v0.1/countries/states/q?country=${encodeURIComponent(countryName)}`
      );
      
      if (response.data && response.data.data && response.data.data.states) {
        setStates(response.data.data.states);
      } else {
        setStates([]);
      }
    } catch (error) {
      console.error('Failed to fetch states:', error);
      setStates([]);
    } finally {
      setLoadingStates(false);
    }
  };

  // Auto-detect user's country from IP
  const detectUserCountry = async () => {
    try {
      // Using ipapi.co free tier for IP geolocation
      const response = await axios.get('https://ipapi.co/json/', { timeout: 5000 });
      const countryCode = response.data.country_code;
      
      if (countryCode) {
        // Check if this country code exists in our list
        const country = ALL_COUNTRIES.find(c => c.code === countryCode);
        if (country) {
          setShippingAddress(prev => ({
            ...prev,
            country: countryCode,
            state: ''
          }));
          fetchStates(countryCode);
          toast.success(`Location detected: ${country.name}`);
        }
      }
    } catch (error) {
      console.error('Failed to detect location:', error);
      // Silent fail - user can manually select country
    } finally {
      setDetectingLocation(false);
    }
  };

  useEffect(() => {
    if (!cart.items || cart.items.length === 0) {
      navigate('/cart');
    } else {
      // Track checkout initiation for analytics
      trackInitiateCheckout(cart, getCartTotal());
    }
    fetchEnabledGateways();
    fetchCheckoutSettings(); // Fetch checkout page settings
    detectUserCountry(); // Auto-detect user's country
    fetchRecommendedProducts(); // Fetch similar products
  }, [cart, navigate]);

  // Fetch checkout settings (promo code visibility, delivery options)
  const fetchCheckoutSettings = async () => {
    try {
      const [settingsRes, optionsRes] = await Promise.all([
        axios.get(`${API}/checkout-settings`),
        axios.get(`${API}/delivery-options`)
      ]);
      setCheckoutSettings(settingsRes.data);
      setDeliveryOptions(optionsRes.data);
      
      // Auto-select first delivery option if available
      if (optionsRes.data.length > 0) {
        setSelectedDeliveryOption(optionsRes.data[0]);
      }
    } catch (error) {
      console.error('Failed to fetch checkout settings:', error);
    }
  };

  // Fetch recommended products (different from cart items)
  const fetchRecommendedProducts = async () => {
    try {
      const response = await axios.get(`${API}/products?limit=4`);
      // Filter out products already in cart
      const cartProductIds = cart.items?.map(item => item.product_id) || [];
      const filtered = response.data.filter(p => !cartProductIds.includes(p.id)).slice(0, 4);
      setRecommendedProducts(filtered);
    } catch (error) {
      console.error('Failed to fetch recommended products:', error);
    }
  };

  // Reset payment method when country changes
  useEffect(() => {
    if (paymentMethod === 'paysafe' && !isNigerian) {
      // If user switches from Nigeria and had paysafe selected, reset
      const firstAvailable = enabledGateways.find(g => g.slug !== 'paysafe');
      setPaymentMethod(firstAvailable?.slug || '');
    }
    // Reset selected payment method when country changes
    setSelectedPaymentMethod('');
    setPaymentMethod('');
  }, [shippingAddress.country, enabledGateways]);

  // Get available payment methods for user's billing currency
  const getAvailablePaymentMethods = () => {
    const billingCountryCode = billingSameAsShipping ? shippingAddress.country : billingAddress.country;
    if (!billingCountryCode) return [];
    
    const billingCurrency = getCurrencyForCountry(billingCountryCode);
    const currencyCode = billingCurrency?.code || 'USD';
    
    // Build reverse mapping: payment_method -> [gateways]
    const methodToGateways = {};
    
    enabledGateways.forEach(gateway => {
      const supported = GATEWAY_CURRENCIES[gateway.slug] || [];
      
      // Only include gateway if it supports the billing currency or USD
      if (!supported.includes(currencyCode) && !supported.includes('USD')) {
        return;
      }
      
      // Skip paysafe for non-Nigerians
      if (gateway.slug === 'paysafe' && !isNigerian) {
        return;
      }
      
      // Get payment methods for this gateway/currency
      const methods = GATEWAY_PAYMENT_METHODS[gateway.slug]?.[currencyCode] || 
                      GATEWAY_PAYMENT_METHODS[gateway.slug]?.default || 
                      ['Card'];
      
      methods.forEach(method => {
        if (!methodToGateways[method]) {
          methodToGateways[method] = [];
        }
        methodToGateways[method].push({
          ...gateway,
          supportedCurrency: supported.includes(currencyCode) ? currencyCode : 'USD'
        });
      });
    });
    
    // Convert to array and sort by popularity
    const methodPriority = ['Card', 'Bank Transfer', 'Mobile Money', 'M-Pesa', 'USSD', 'PayPal Balance', 'Apple Pay', 'Google Pay', 'Barter', 'SEPA', 'iDEAL'];
    
    return Object.entries(methodToGateways)
      .map(([method, gateways]) => ({ method, gateways }))
      .sort((a, b) => {
        const aIndex = methodPriority.indexOf(a.method);
        const bIndex = methodPriority.indexOf(b.method);
        return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
      });
  };

  // Handle payment method selection
  const handlePaymentMethodSelect = (method, gateways) => {
    setSelectedPaymentMethod(method);
    
    if (gateways.length === 1) {
      // Only one gateway provides this method - select it and auto-proceed
      const selectedGateway = gateways[0].slug;
      setPaymentMethod(selectedGateway);
      setShowGatewaySelector(false);
      // Auto-proceed to payment
      setTimeout(() => processPaymentWithGateway(selectedGateway), 100);
    } else {
      // Multiple gateways - show selector with countdown
      setAvailableGatewaysForMethod(gateways);
      setShowGatewaySelector(true);
      setCountdownSeconds(3);
      
      // Start countdown
      const countdownInterval = setInterval(() => {
        setCountdownSeconds(prev => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            // Auto-select most popular gateway for this region and proceed
            const autoSelected = selectBestGateway(gateways);
            setPaymentMethod(autoSelected.slug);
            setShowGatewaySelector(false);
            // Auto-proceed to payment
            setTimeout(() => processPaymentWithGateway(autoSelected.slug), 100);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      setGatewaySelectionTimeout(countdownInterval);
    }
  };

  // Select best gateway based on region popularity
  const selectBestGateway = (gateways) => {
    const billingCountryCode = billingSameAsShipping ? shippingAddress.country : billingAddress.country;
    
    // Regional preferences
    const regionalPreferences = {
      'NG': ['paystack', 'flutterwave', 'bachs', 'korapay', 'squad'],
      'GH': ['paystack', 'flutterwave'],
      'KE': ['flutterwave', 'paystack'],
      'ZA': ['paystack', 'flutterwave'],
      'US': ['flutterwave', 'bachs', 'paystack'],
      'GB': ['flutterwave', 'bachs', 'paystack'],
      'EU': ['flutterwave', 'bachs', 'paystack'],
    };
    
    const preferences = regionalPreferences[billingCountryCode] || regionalPreferences['US'];
    
    // Find the first gateway from preferences that's available
    for (const slug of preferences) {
      const found = gateways.find(g => g.slug === slug);
      if (found) return found;
    }
    
    // Fallback to random selection
    return gateways[Math.floor(Math.random() * gateways.length)];
  };

  // Handle gateway selection from modal
  const handleGatewaySelect = (gateway) => {
    if (gatewaySelectionTimeout) {
      clearInterval(gatewaySelectionTimeout);
      setGatewaySelectionTimeout(null);
    }
    setPaymentMethod(gateway.slug);
    setShowGatewaySelector(false);
    // Auto-proceed to payment after selection
    setTimeout(() => processPaymentWithGateway(gateway.slug), 100);
  };

  const fetchEnabledGateways = async () => {
    try {
      const response = await axios.get(`${API}/payment-gateways/enabled`);
      const gateways = response.data;
      setEnabledGateways(gateways);
      
      // Set default payment method to first enabled gateway (excluding paysafe for non-Nigerians)
      if (gateways.length > 0) {
        const defaultGateway = gateways.find(g => g.slug !== 'paysafe') || gateways[0];
        setPaymentMethod(defaultGateway.slug);
      }
    } catch (error) {
      console.error('Failed to fetch payment gateways:', error);
      // Fallback to stripe if API fails
      setEnabledGateways([{ slug: 'stripe', name: 'Stripe', is_enabled: true }]);
      setPaymentMethod('stripe');
    } finally {
      setLoadingGateways(false);
    }
  };

  const handleInputChange = (e) => {
    setShippingAddress({
      ...shippingAddress,
      [e.target.name]: e.target.value
    });
  };

  const handleCountryChange = (value) => {
    setShippingAddress({
      ...shippingAddress,
      country: value,
      state: '' // Reset state when country changes
    });
    fetchStates(value);
  };

  const handleStateChange = (value) => {
    setShippingAddress({
      ...shippingAddress,
      state: value
    });
  };

  // Validate form before processing payment
  const validateCheckoutForm = () => {
    // Validate shipping address (postal code intentionally not required)
    if (!shippingAddress.full_name || !shippingAddress.address_line1 || 
        !shippingAddress.city || !shippingAddress.state || 
        !shippingAddress.country) {
      toast.error('Please complete all shipping address fields');
      return false;
    }

    // Validate guest info if not logged in
    if (isGuest) {
      if (!guestInfo.email || !guestInfo.email.includes('@')) {
        toast.error('Please enter a valid email address');
        return false;
      }
      if (!guestInfo.phone || guestInfo.phone.length < 7) {
        toast.error('Please enter a valid phone number');
        return false;
      }
    }

    // Validate cart
    if (!cart.items || cart.items.length === 0) {
      toast.error('Your cart is empty');
      return false;
    }

    return true;
  };

  // Process payment with a specific gateway (called after method/gateway selection)
  const processPaymentWithGateway = async (gateway) => {
    // Validate form first
    if (!validateCheckoutForm()) {
      return;
    }

    if (loading) return; // Prevent double submissions
    
    setLoading(true);

    try {
      // Create order (guest or authenticated)
      let orderResponse;
      
      if (isGuest) {
        // Guest checkout
        const orderPayload = {
          items: cart.items,
          shipping_address: shippingAddress,
          payment_method: gateway,
          coupon_code: appliedCoupon?.code || null,
          guest_email: guestInfo.email,
          guest_phone: guestInfo.phone,
          is_guest: true,
          delivery_option_id: selectedDeliveryOption?.id || null,
          referral_code: getReferralCode(referralInput)
        };
        
        orderResponse = await axios.post(
          `${API}/orders/create-guest`,
          orderPayload
        );
      } else {
        // Authenticated user checkout
        const orderPayload = {
          items: cart.items,
          shipping_address: shippingAddress,
          payment_method: gateway,
          coupon_code: appliedCoupon?.code || null,
          delivery_option_id: selectedDeliveryOption?.id || null,
          referral_code: getReferralCode(referralInput)
        };
        
        orderResponse = await axios.post(
          `${API}/orders/create`,
          orderPayload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      const order = orderResponse.data;
      const customerEmail = isGuest ? guestInfo.email : user.email;

      // Get billing country to determine payment currency
      const billingCountryCode = billingSameAsShipping ? shippingAddress.country : billingAddress.country;
      
      // Helper to get payment details for each gateway
      const getPaymentDetails = (gatewaySlug) => {
        const customerCurrency = getCurrencyForCountry(billingCountryCode);
        const supported = GATEWAY_CURRENCIES[gatewaySlug] || [];
        
        // If user has chosen a specific currency, use it
        if (chosenPaymentCurrency && supported.includes(chosenPaymentCurrency)) {
          const amount = convertPrice(getFinalTotal(), exchangeRates, chosenPaymentCurrency);
          return { currency: chosenPaymentCurrency, amount };
        }
        
        // Check if gateway supports customer's currency
        if (supported.includes(customerCurrency.code)) {
          const amount = convertPrice(getFinalTotal(), exchangeRates, customerCurrency.code);
          return { currency: customerCurrency.code, amount };
        }
        
        // Fallback to the store base currency (NGN)
        if (supported.includes('NGN')) {
          return { currency: 'NGN', amount: getFinalTotal() };
        }
        
        // Last resort: first supported currency (shouldn't happen for valid gateways)
        const fallbackCurrency = supported[0] || 'NGN';
        const amount = convertPrice(getFinalTotal(), exchangeRates, fallbackCurrency);
        return { currency: fallbackCurrency, amount };
      };

      // Handle payment based on gateway
      if (gateway === 'stripe') {
        const originUrl = window.location.origin;
        const stripeEndpoint = isGuest 
          ? `${API}/payments/stripe/create-session-guest?order_id=${order.id}&origin_url=${originUrl}&email=${customerEmail}`
          : `${API}/payments/stripe/create-session?order_id=${order.id}&origin_url=${originUrl}&email=${customerEmail}`;
        
        const stripeResponse = await axios.post(
          stripeEndpoint,
          {},
          { headers: token ? { Authorization: `Bearer ${token}` } : {} }
        );
        
        clearCart();
        window.location.href = stripeResponse.data.url;
      } else if (gateway === 'paystack') {
        const payment = getPaymentDetails('paystack');
        const paystackResponse = await axios.post(
          `${API}/payments/paystack/initialize-guest`,
          {
            order_id: order.id,
            email: customerEmail,
            amount: payment.amount,
            currency: payment.currency,
            callback_url: `${window.location.origin}/order-complete/${order.id}`
          }
        );
        
        clearCart();
        window.location.href = paystackResponse.data.authorization_url;
      } else if (gateway === 'flutterwave') {
        const payment = getPaymentDetails('flutterwave');
        const flutterwaveResponse = await axios.post(
          `${API}/payments/flutterwave/initialize-guest`,
          {
            order_id: order.id,
            email: customerEmail,
            amount: payment.amount,
            currency: payment.currency,
            customer_name: shippingAddress.full_name,
            customer_phone: isGuest ? guestInfo.phone : '',
            callback_url: `${window.location.origin}/order-complete/${order.id}`
          }
        );
        
        clearCart();
        window.location.href = flutterwaveResponse.data.authorization_url;
      } else if (gateway === 'korapay') {
        const payment = getPaymentDetails('korapay');
        const korapayResponse = await axios.post(
          `${API}/payments/korapay/initialize-guest`,
          {
            order_id: order.id,
            email: customerEmail,
            amount: payment.amount,
            currency: payment.currency,
            customer_name: shippingAddress.full_name,
            callback_url: `${window.location.origin}/order-complete/${order.id}`
          }
        );
        
        clearCart();
        window.location.href = korapayResponse.data.authorization_url;
      } else if (gateway === 'squad' || gateway === 'bachs') {
        const payment = getPaymentDetails(gateway);
        const response = await axios.post(
          `${API}/payments/${gateway}/initialize-guest`,
          {
            order_id: order.id,
            email: customerEmail,
            currency: payment.currency,
            customer_name: shippingAddress.full_name
          }
        );
        clearCart();
        window.location.href = response.data.authorization_url;
      } else if (gateway === 'monnify') {
        // Monnify only supports NGN
        const ngnAmount = convertPrice(getFinalTotal(), exchangeRates, 'NGN');
        const monnifyResponse = await axios.post(
          `${API}/payments/monnify/initialize-guest`,
          {
            order_id: order.id,
            email: customerEmail,
            amount: ngnAmount,
            customer_name: shippingAddress.full_name,
            callback_url: `${window.location.origin}/order-complete/${order.id}`
          }
        );
        
        clearCart();
        window.location.href = monnifyResponse.data.authorization_url;
      } else if (gateway === 'paypal') {
        const payment = getPaymentDetails('paypal');
        const paypalResponse = await axios.post(
          `${API}/payments/paypal/initialize-guest`,
          {
            order_id: order.id,
            amount: payment.amount,
            currency: payment.currency,
            callback_url: `${window.location.origin}/order-complete/${order.id}?gateway=paypal`
          }
        );
        
        clearCart();
        window.location.href = paypalResponse.data.authorization_url;
      } else if (gateway === 'paysafe') {
        // Paysafe only supports NGN
        const ngnAmount = convertPrice(getFinalTotal(), exchangeRates, 'NGN');
        clearCart();
        const paysafeParams = new URLSearchParams({
          email: customerEmail,
          name: shippingAddress.full_name,
          amount: String(Math.round(ngnAmount)),
          billing_address: `${shippingAddress.address_line1}, ${shippingAddress.city}, ${shippingAddress.state} ${shippingAddress.postal_code}, Nigeria`,
          callback: `${window.location.origin}/api/paysafe/webhook`,
          return_url: `${window.location.origin}/order-complete/${order.id}`
        });
        window.location.href = `https://paysafegateway.lovable.app/pay?${paysafeParams.toString()}`;
      } else {
        clearCart();
        toast.error('Payment gateway not configured');
        navigate(`/order-complete/${order.id}`);
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Failed to process checkout');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!paymentMethod) {
      toast.error('Please select a payment method');
      return;
    }

    // Use the shared payment processing function
    await processPaymentWithGateway(paymentMethod);
  };

  // Filter gateways - show paysafe only for Nigerians, and put it first
  const availableGateways = useMemo(() => {
    const filtered = enabledGateways.filter(gateway => {
      if (gateway.slug === 'paysafe') {
        return isNigerian;
      }
      return true;
    });
    
    // Sort to put paysafe first for Nigerian customers
    if (isNigerian) {
      return filtered.sort((a, b) => {
        if (a.slug === 'paysafe') return -1;
        if (b.slug === 'paysafe') return 1;
        return 0;
      });
    }
    
    return filtered;
  }, [enabledGateways, isNigerian]);

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-12 text-gray-900">
        <h1 className="font-heading text-4xl font-bold mb-12" data-testid="checkout-page-title">Checkout</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Checkout Form - Shows second on mobile, first on desktop */}
          <div className="lg:col-span-2 order-2 lg:order-1">
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Guest Contact Info */}
              {isGuest && (
                <div className="mb-8 p-4 bg-health-mint border border-health-line rounded-2xl">
                  <h2 className="font-heading text-lg font-bold mb-4">Contact Information</h2>
                  <p className="text-sm text-gray-600 mb-4">Please provide your contact details for order updates</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="guest_email">Email Address *</Label>
                      <Input
                        id="guest_email"
                        type="email"
                        value={guestInfo.email}
                        onChange={(e) => setGuestInfo({ ...guestInfo, email: e.target.value })}
                        required
                        placeholder="your@email.com"
                        className="mt-1"
                        data-testid="guest-email"
                      />
                    </div>
                    <div>
                      <Label htmlFor="guest_phone">Phone Number *</Label>
                      <Input
                        id="guest_phone"
                        type="tel"
                        value={guestInfo.phone}
                        onChange={(e) => setGuestInfo({ ...guestInfo, phone: e.target.value })}
                        required
                        placeholder="+1234567890"
                        className="mt-1"
                        data-testid="guest-phone"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Shipping Address */}
              <div>
                <h2 className="font-heading text-xl font-bold mb-6">Shipping Address</h2>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="full_name">Full Name</Label>
                    <Input
                      id="full_name"
                      name="full_name"
                      value={shippingAddress.full_name}
                      onChange={handleInputChange}
                      required
                      className="mt-1 border-0 border-b border-gray-200 rounded-none focus:ring-0 focus:border-black"
                      data-testid="shipping-fullname"
                    />
                  </div>
                  <div>
                    <Label htmlFor="address_line1">Address</Label>
                    <Input
                      id="address_line1"
                      name="address_line1"
                      value={shippingAddress.address_line1}
                      onChange={handleInputChange}
                      required
                      className="mt-1 border-0 border-b border-gray-200 rounded-none focus:ring-0 focus:border-black"
                      data-testid="shipping-address"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="city">City</Label>
                      <Input
                        id="city"
                        name="city"
                        value={shippingAddress.city}
                        onChange={handleInputChange}
                        required
                        className="mt-1 border-0 border-b border-gray-200 rounded-none focus:ring-0 focus:border-black"
                        data-testid="shipping-city"
                      />
                    </div>
                    <div>
                      <Label htmlFor="state">State/Province</Label>
                      {loadingStates ? (
                        <div className="mt-1 h-10 flex items-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black mr-2"></div>
                          <span className="text-sm text-gray-500">Loading states...</span>
                        </div>
                      ) : states.length > 0 ? (
                        <Select value={shippingAddress.state} onValueChange={handleStateChange}>
                          <SelectTrigger className="mt-1 border-0 border-b border-gray-200 rounded-none focus:ring-0" data-testid="shipping-state">
                            <SelectValue placeholder="Select state/province" />
                          </SelectTrigger>
                          <SelectContent className="max-h-[300px]">
                            {states.map((state) => (
                              <SelectItem key={state.state_code || state.name} value={state.name}>
                                {state.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          id="state"
                          name="state"
                          value={shippingAddress.state}
                          onChange={handleInputChange}
                          required
                          placeholder={shippingAddress.country ? "Enter state/province" : "Select country first"}
                          className="mt-1 border-0 border-b border-gray-200 rounded-none focus:ring-0 focus:border-black"
                          data-testid="shipping-state"
                        />
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <Label htmlFor="country" className="flex items-center gap-2">
                        Country
                        {detectingLocation && (
                          <span className="flex items-center text-xs text-blue-600">
                            <MapPin className="h-3 w-3 mr-1 animate-pulse" />
                            Detecting...
                          </span>
                        )}
                      </Label>
                      <Select
                        value={shippingAddress.country}
                        onValueChange={(value) => handleCountryChange(value)}
                      >
                        <SelectTrigger className="mt-1 border-0 border-b border-gray-200 rounded-none focus:ring-0" data-testid="shipping-country-select">
                          <SelectValue placeholder="Select country" />
                        </SelectTrigger>
                        <SelectContent className="max-h-60">
                          {ALL_COUNTRIES.map((country) => (
                            <SelectItem key={country.code} value={country.code}>
                              {country.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Billing Address */}
              <div>
                <h2 className="font-heading text-xl font-bold mb-4">Billing Address</h2>
                <div className="flex items-center space-x-2 mb-4">
                  <Checkbox
                    id="billing-same"
                    checked={billingSameAsShipping}
                    onCheckedChange={(checked) => setBillingSameAsShipping(checked)}
                  />
                  <Label htmlFor="billing-same" className="text-sm font-normal cursor-pointer">
                    Same as shipping address
                  </Label>
                </div>

                {!billingSameAsShipping && (
                  <div className="space-y-4 border-t pt-4">
                    <div>
                      <Label htmlFor="billing_full_name">Full Name</Label>
                      <Input
                        id="billing_full_name"
                        value={billingAddress.full_name}
                        onChange={(e) => setBillingAddress({...billingAddress, full_name: e.target.value})}
                        required
                        className="mt-1 border-0 border-b border-gray-200 rounded-none focus:ring-0 focus:border-black"
                      />
                    </div>
                    <div>
                      <Label htmlFor="billing_address">Address</Label>
                      <Input
                        id="billing_address"
                        value={billingAddress.address_line1}
                        onChange={(e) => setBillingAddress({...billingAddress, address_line1: e.target.value})}
                        required
                        className="mt-1 border-0 border-b border-gray-200 rounded-none focus:ring-0 focus:border-black"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="billing_city">City</Label>
                        <Input
                          id="billing_city"
                          value={billingAddress.city}
                          onChange={(e) => setBillingAddress({...billingAddress, city: e.target.value})}
                          required
                          className="mt-1 border-0 border-b border-gray-200 rounded-none focus:ring-0 focus:border-black"
                        />
                      </div>
                      <div>
                        <Label htmlFor="billing_state">State/Province</Label>
                        <Input
                          id="billing_state"
                          value={billingAddress.state}
                          onChange={(e) => setBillingAddress({...billingAddress, state: e.target.value})}
                          required
                          className="mt-1 border-0 border-b border-gray-200 rounded-none focus:ring-0 focus:border-black"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <Label htmlFor="billing_country">Country</Label>
                        <Select
                          value={billingAddress.country}
                          onValueChange={(value) => setBillingAddress({...billingAddress, country: value})}
                        >
                          <SelectTrigger className="mt-1 border-0 border-b border-gray-200 rounded-none focus:ring-0" data-testid="billing-country-select">
                            <SelectValue placeholder="Select country" />
                          </SelectTrigger>
                          <SelectContent className="max-h-60">
                            {ALL_COUNTRIES.map((country) => (
                              <SelectItem key={country.code} value={country.code}>
                                {country.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Payment Method */}
              <div>
                <h2 className="font-heading text-xl font-bold mb-6">Payment Method</h2>
                
                {loadingGateways ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black mx-auto"></div>
                    <p className="text-sm text-gray-500 mt-2">Loading payment options...</p>
                  </div>
                ) : !shippingAddress.country ? (
                  <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
                    <Globe className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600 font-medium mb-2">Choose your country to see available payment methods</p>
                    <p className="text-sm text-gray-500">Payment options vary by region</p>
                    
                    {/* Show all payment logos as preview */}
                    <div className="mt-6 flex flex-wrap justify-center gap-4 px-4">
                      {Object.entries(GATEWAY_LOGOS).filter(([key]) => RETAINED_GATEWAYS.includes(key)).map(([key, logo]) => (
                        <div key={key} className="opacity-50 grayscale">
                          {logo}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : availableGateways.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <p>No payment methods available for your region.</p>
                    <p className="text-sm mt-2">Please contact support.</p>
                  </div>
                ) : (
                  <div className="space-y-3" data-testid="payment-methods">
                    {/* Payment Method Selection */}
                    {getAvailablePaymentMethods().map(({ method, gateways }) => {
                      const config = PAYMENT_METHOD_CONFIG[method] || {
                        icon: CreditCard,
                        label: method,
                        description: '',
                        color: 'bg-gray-50 border-gray-200 hover:border-gray-400'
                      };
                      const Icon = config.icon;
                      const isSelected = selectedPaymentMethod === method;
                      
                      // Calculate price for display (use first gateway's currency)
                      const billingCountryCode = billingSameAsShipping ? shippingAddress.country : billingAddress.country;
                      const billingCurrency = getCurrencyForCountry(billingCountryCode);
                      const gatewayCurrency = gateways[0]?.supportedCurrency || 'USD';
                      const gatewayAmount = gatewayCurrency === 'USD' 
                        ? getFinalTotal()
                        : convertPrice(getFinalTotal(), exchangeRates, gatewayCurrency);
                      const currencyInfo = ALL_CURRENCIES[gatewayCurrency] || { symbol: '$' };
                      
                      return (
                        <button
                          key={method}
                          type="button"
                          onClick={() => handlePaymentMethodSelect(method, gateways)}
                          className={`w-full flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                            isSelected 
                              ? 'border-black bg-gray-100 shadow-md' 
                              : config.color
                          }`}
                          data-testid={`payment-method-${method.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          <div className={`flex items-center justify-center w-12 h-12 rounded-full ${isSelected ? 'bg-health-emerald text-white' : 'bg-white shadow-sm'}`}>
                            <Icon className="h-6 w-6" />
                          </div>
                          <div className="flex-1 ml-4 text-left">
                            <div className="font-semibold text-gray-900">{config.label}</div>
                            <div className="text-sm text-gray-500">{config.description}</div>
                            {gateways.length > 1 && (
                              <div className="text-xs text-gray-400 mt-1">
                                {gateways.length} providers available
                              </div>
                            )}
                          </div>
                          <div className="text-right">
                            <div className="font-semibold text-gray-800">
                              {currencyInfo.symbol}{gatewayAmount.toLocaleString(undefined, { 
                                minimumFractionDigits: gatewayCurrency === 'NGN' ? 0 : 2, 
                                maximumFractionDigits: 2 
                              })}
                            </div>
                            <div className="text-xs text-gray-500">{gatewayCurrency}</div>
                          </div>
                          {isSelected && (
                            <div className="ml-3">
                              <Check className="h-5 w-5 text-black" />
                            </div>
                          )}
                        </button>
                      );
                    })}
                    
                    {getAvailablePaymentMethods().length === 0 && (
                      <p className="text-gray-500 text-center py-4">
                        No payment methods available for your location. Please select a different country.
                      </p>
                    )}
                  </div>
                )}
                
                {/* Gateway Selection Modal */}
                {showGatewaySelector && (
                  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
                      <h3 className="text-lg font-bold mb-2">Choose Payment Provider</h3>
                      <p className="text-sm text-gray-600 mb-4">
                        Multiple providers support <strong>{selectedPaymentMethod}</strong>. Select your preferred one:
                      </p>
                      
                      <div className="space-y-2 mb-4">
                        {availableGatewaysForMethod.map((gateway) => (
                          <button
                            key={gateway.slug}
                            type="button"
                            onClick={() => handleGatewaySelect(gateway)}
                            className="w-full flex items-center p-3 border-2 rounded-lg hover:border-black hover:bg-gray-50 transition-all"
                          >
                            <div className="flex items-center flex-1">
                              {GATEWAY_LOGOS[gateway.slug] || (
                                <>
                                  {GATEWAY_ICONS[gateway.slug] || <CreditCard className="mr-2 h-5 w-5" />}
                                  <span className="font-medium">{GATEWAY_LABELS[gateway.slug] || gateway.name}</span>
                                </>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                      
                      <div className="text-center text-sm text-gray-500">
                        <div className="flex items-center justify-center gap-2 mb-2">
                          <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center font-bold text-gray-600">
                            {countdownSeconds}
                          </div>
                        </div>
                        Auto-selecting most popular provider in {countdownSeconds}s...
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Currency Mismatch Warning */}
              {paymentMethod && (() => {
                const billingCountryCode = billingSameAsShipping ? shippingAddress.country : billingAddress.country;
                if (!billingCountryCode) return null;
                
                const billingCurrency = getCurrencyForCountry(billingCountryCode);
                const supported = GATEWAY_CURRENCIES[paymentMethod] || [];
                
                // Determine default payment currency
                const defaultPaymentCurrency = supported.includes(billingCurrency.code) 
                  ? billingCurrency.code 
                  : supported.includes('USD') ? 'USD' : supported[0];
                
                // Check if there's a mismatch between display and payment currency
                if (activeCurrency !== defaultPaymentCurrency && !chosenPaymentCurrency) {
                  const displayAmount = convertPrice(getFinalTotal(), exchangeRates, activeCurrency);
                  const paymentAmount = convertPrice(getFinalTotal(), exchangeRates, defaultPaymentCurrency);
                  
                  const displaySymbol = ALL_CURRENCIES[activeCurrency]?.symbol || '$';
                  const paymentSymbol = ALL_CURRENCIES[defaultPaymentCurrency]?.symbol || '$';
                  
                  const gatewayNames = {
                    stripe: 'Stripe',
                    paypal: 'PayPal',
                    paystack: 'Paystack',
                    flutterwave: 'Flutterwave',
                    korapay: 'Korapay',
                    monnify: 'Monnify',
                    paysafe: 'Paysafe'
                  };
                  
                  // Only show warning if gateway supports both currencies
                  const canUseDisplayCurrency = supported.includes(activeCurrency);
                  
                  if (canUseDisplayCurrency) {
                    return (
                      <CurrencyMismatchWarning
                        displayCurrency={activeCurrency}
                        displayAmount={`${displaySymbol}${displayAmount.toLocaleString(undefined, { maximumFractionDigits: 2 })}`}
                        paymentCurrency={defaultPaymentCurrency}
                        paymentAmount={`${paymentSymbol}${paymentAmount.toLocaleString(undefined, { maximumFractionDigits: 2 })}`}
                        gatewayName={gatewayNames[paymentMethod] || paymentMethod}
                        isOpen={true}
                        onChooseDisplay={() => setChosenPaymentCurrency(activeCurrency)}
                        onChoosePayment={() => setChosenPaymentCurrency(defaultPaymentCurrency)}
                        onClose={() => setChosenPaymentCurrency(defaultPaymentCurrency)}
                      />
                    );
                  }
                }
                return null;
              })()}

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={loading || !paymentMethod || availableGateways.length === 0}
                className="w-full bg-health-emerald text-white hover:bg-health-emerald-dark rounded-full py-7 text-base font-bold shadow-lift"
                data-testid="place-order-button"
              >
                {loading ? 'Processing...' : 'Place Order'}
              </Button>
            </form>
          </div>

          {/* Order Summary - Shows first on mobile, last on desktop */}
          <div className="lg:col-span-1 order-1 lg:order-2">
            <div className="bg-gray-50 p-8 lg:sticky lg:top-24">
              <h2 className="font-heading text-xl font-bold mb-6">Order Summary</h2>
              
              <div className="space-y-4 mb-6">
                {cart.items?.map((item) => (
                  <div key={item.product_id} className="flex justify-between text-sm">
                    <span className="text-gray-600">{item.name || 'Item'} x {item.quantity}</span>
                    <DualPrice amount={item.price * item.quantity} className="font-medium" />
                  </div>
                ))}
              </div>

              {/* Coupon Code Section - Conditionally Shown */}
              <div className="border-t border-gray-300 pt-4 mb-4" data-testid="referral-code-section">
                <Label htmlFor="referral-code" className="text-sm font-medium mb-2 block">Ambassador / referral code (optional)</Label>
                <Input
                  id="referral-code"
                  value={referralInput}
                  onChange={(e) => setReferralInput(e.target.value.toUpperCase())}
                  placeholder="e.g. ADA123"
                  maxLength={20}
                  data-testid="referral-code-input"
                />
              </div>

              {checkoutSettings.show_promo_code && (
                <div className="border-t border-gray-300 pt-4 mb-4" data-testid="promo-code-section">
                  <Label className="text-sm font-medium mb-2 block">Promo Code</Label>
                  {appliedCoupon ? (
                    <div className="flex items-center justify-between bg-green-50 border border-green-200 p-3 rounded">
                      <div className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-green-600" />
                        <span className="font-mono font-bold text-green-800">{appliedCoupon.code}</span>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={handleRemoveCoupon}
                        className="text-red-600 hover:text-red-800 hover:bg-red-50 p-1 h-auto"
                        data-testid="remove-coupon-btn"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Input
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                        placeholder="Enter code"
                        className="flex-1 uppercase"
                        data-testid="coupon-input"
                      />
                      <Button
                        type="button"
                        onClick={handleApplyCoupon}
                        disabled={applyingCoupon || !couponCode.trim()}
                        variant="outline"
                        className="px-4"
                        data-testid="apply-coupon-btn"
                      >
                        {applyingCoupon ? '...' : 'Apply'}
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* Delivery Options Section - Conditionally Shown */}
              {checkoutSettings.show_delivery_options && deliveryOptions.length > 0 && (
                <div className="border-t border-gray-300 pt-4 mb-4" data-testid="delivery-options-section">
                  <Label className="text-sm font-medium mb-3 block">Delivery Option</Label>
                  <div className="space-y-2">
                    {deliveryOptions.map((option) => (
                      <label
                        key={option.id}
                        className={`flex items-center justify-between p-3 border rounded-lg cursor-pointer transition-colors ${
                          selectedDeliveryOption?.id === option.id 
                            ? 'border-black bg-gray-50' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        data-testid={`delivery-option-${option.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <input
                            type="radio"
                            name="delivery-option"
                            checked={selectedDeliveryOption?.id === option.id}
                            onChange={() => setSelectedDeliveryOption(option)}
                            className="h-4 w-4 text-black focus:ring-black"
                          />
                          <div>
                            <p className="font-medium text-sm">{option.name}</p>
                            <p className="text-xs text-gray-500">
                              {option.description || `${option.estimated_days_min}-${option.estimated_days_max} business days`}
                            </p>
                          </div>
                        </div>
                        <span className="font-semibold text-sm">
                          {option.price === 0 ? 'Free' : <DualPrice amount={option.price} />}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div className="border-t border-gray-300 pt-4 mb-4">
                <div className="flex justify-between text-sm text-gray-600 mb-2">
                  <span>Subtotal</span>
                  <DualPrice amount={getCartTotal()} />
                </div>
                
                {appliedCoupon && (
                  <div className="flex justify-between text-sm text-green-600 mb-2">
                    <span>Discount ({appliedCoupon.discount_type === 'percentage' ? `${appliedCoupon.discount_value}%` : 'Fixed'})</span>
                    <span>-${appliedCoupon.discount_amount.toFixed(2)}</span>
                  </div>
                )}
                
                {/* Delivery Fee */}
                {selectedDeliveryOption && selectedDeliveryOption.price > 0 && (
                  <div className="flex justify-between text-sm text-gray-600 mb-2">
                    <span>Delivery ({selectedDeliveryOption.name})</span>
                    <DualPrice amount={selectedDeliveryOption.price} />
                  </div>
                )}
                
                {/* Total with multiple currencies */}
                <div className="mt-4 pt-2 border-t border-gray-200">
                  <CheckoutPrices 
                    amount={getFinalTotal()} 
                    billingCountryCode={billingSameAsShipping ? shippingAddress.country : billingAddress.country}
                    className="text-right"
                    data-testid="checkout-total"
                  />
                </div>
                
                {/* Show NGN equivalent if Nigerian */}
                {isNigerian && (
                  <div className="flex justify-between mt-2 text-sm text-gray-600">
                    <span>Payment Amount (NGN)</span>
                    <span className="font-medium" data-testid="checkout-total-ngn">
                      ₦{getTotalInNGN().toLocaleString()}
                    </span>
                  </div>
                )}
              </div>

              {/* Exchange rate notice */}
              {isNigerian && (
                <p className="text-xs text-gray-500 mb-4">
                  {loadingRate ? 'Loading exchange rate...' : `Exchange rate: $1 = ₦${exchangeRate.toLocaleString()}`}
                </p>
              )}

              {/* Estimated Delivery */}
              <div className="p-4 bg-green-50 border border-green-200 mb-4">
                <p className="text-xs font-medium mb-1 text-green-800">Estimated Delivery</p>
                <p className="text-sm font-semibold text-green-900" data-testid="estimated-delivery">
                  {estimatedDelivery}
                </p>
              </div>

              <div className="p-4 bg-health-mint border border-health-line rounded-2xl">
                <p className="text-xs font-medium mb-2">Shipping</p>
                <p className="text-xs text-gray-600">
                  Ships worldwide with fast, tracked delivery
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Recommended Products Section */}
        {recommendedProducts.length > 0 && (
          <div className="mt-16 border-t pt-12" data-testid="checkout-recommendations">
            <h2 className="font-heading text-xl font-bold mb-6">You May Also Like</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {recommendedProducts.map((product) => (
                <Link
                  key={product.id}
                  to={`/products/${product.id}`}
                  className="group"
                >
                  <div className="aspect-square bg-gray-100 mb-3 overflow-hidden">
                    <img
                      src={product.images?.[0] || product.image_url || '/placeholder.png'}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                    />
                  </div>
                  <h3 className="font-medium text-sm truncate group-hover:text-gray-600">
                    {product.name}
                  </h3>
                  <p className="text-sm font-bold mt-1">${product.price?.toFixed(2)}</p>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CheckoutPage;
